import React, { useState, useEffect } from "react";
import {
  Box,
  Heading,
  useToast,
  Button,
  HStack,
  VStack,
  Grid,
  GridItem,
  Text,
  Divider,
  Skeleton,
  Badge,
  Card,
  CardBody,
  CardHeader,
  Flex,
  useColorModeValue,
  Icon,
} from "@chakra-ui/react";
import { useNavigate, useParams } from "react-router-dom";
import {
  BsArrowLeft,
  BsPencil,
  BsCheckCircle,
  BsXCircle,
  BsArrowCounterclockwise,
  BsCircleFill,
  BsCheckCircleFill,
  BsXCircleFill,
} from "react-icons/bs";
import procurementApi from "../services/procurementApi";
import StatusBadge from "../components/StatusBadge";
import LineItemsTable from "../components/LineItemsTable";
import VendorPOProposalList from "../components/VendorPOProposalList";

const RequestDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [request, setRequest] = useState(null);

  const bgColor = useColorModeValue("white", "gray.800");

  useEffect(() => {
    fetchRequest();
  }, [id]);

  const fetchRequest = async () => {
    try {
      setLoading(true);
      const response = await procurementApi.getRequestById(id);
      const requestData = response?.data || response;
      if (!requestData) {
        throw new Error("Request not found");
      }
      setRequest(requestData);
    } catch (error) {
      toast({
        title: "Error fetching request",
        description: error.response?.data?.message || error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      navigate("/procurement/requests");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    navigate(`/procurement/requests/${id}/edit`);
  };

  const handleSubmit = async () => {
    try {
      await procurementApi.submitRequest(id);
      toast({
        title: "Request submitted",
        status: "success",
        duration: 2000,
      });
      fetchRequest();
    } catch (error) {
      toast({
        title: "Error submitting request",
        description: error.response?.data?.message || error.message,
        status: "error",
        duration: 3000,
      });
    }
  };

  const handleApprove = async () => {
    try {
      await procurementApi.approveRequest(id, { comments: "Approved" });
      toast({
        title: "Request approved",
        status: "success",
        duration: 2000,
      });
      fetchRequest();
    } catch (error) {
      toast({
        title: "Error approving request",
        description: error.response?.data?.message || error.message,
        status: "error",
        duration: 3000,
      });
    }
  };

  const handleReject = async () => {
    const reason = window.prompt("Reason for rejection:");
    if (!reason) return;

    try {
      await procurementApi.rejectRequest(id, { reason });
      toast({
        title: "Request rejected",
        status: "success",
        duration: 2000,
      });
      fetchRequest();
    } catch (error) {
      toast({
        title: "Error rejecting request",
        description: error.response?.data?.message || error.message,
        status: "error",
        duration: 3000,
      });
    }
  };

  if (loading) {
    return (
      <Box p={6}>
        <Skeleton height="40px" mb={4} />
        <Skeleton height="600px" />
      </Box>
    );
  }

  if (!request) {
    return (
      <Box p={6}>
        <Text>Request not found</Text>
      </Box>
    );
  }

  return (
    <Box p={6}>
      {/* Header */}
      <Flex justify="space-between" align="center" mb={6}>
        <HStack>
          <Button
            leftIcon={<BsArrowLeft />}
            variant="ghost"
            onClick={() => navigate("/procurement/requests")}
          >
            Back
          </Button>
          <Heading size="lg">Request Details</Heading>
        </HStack>
        <HStack>
          {request.status === "DRAFT" && (
            <>
              <Button
                leftIcon={<BsPencil />}
                variant="outline"
                onClick={handleEdit}
              >
                Edit
              </Button>
              <Button
                leftIcon={<BsCheckCircle />}
                colorScheme="blue"
                onClick={handleSubmit}
              >
                Submit for Approval
              </Button>
            </>
          )}
          {(request.status === "SUBMITTED" ||
            request.status === "IN_REVIEW") && (
            <>
              <Button
                leftIcon={<BsCheckCircle />}
                colorScheme="green"
                onClick={handleApprove}
              >
                Approve
              </Button>
              <Button
                leftIcon={<BsXCircle />}
                colorScheme="red"
                onClick={handleReject}
              >
                Reject
              </Button>
            </>
          )}
        </HStack>
      </Flex>

      {/* Basic Info Card */}
      <Card mb={6} bg={bgColor}>
        <CardHeader>
          <Flex justify="space-between" align="center">
            <Heading size="md">{request.title}</Heading>
            <StatusBadge status={request.status} />
          </Flex>
        </CardHeader>
        <CardBody>
          <Grid templateColumns="repeat(3, 1fr)" gap={6}>
            <GridItem>
              <Text fontWeight="semibold" fontSize="sm" color="gray.600">
                Request ID
              </Text>
              <Text>
                {request.requestId || request._id?.slice(-8) || "N/A"}
              </Text>
            </GridItem>
            <GridItem>
              <Text fontWeight="semibold" fontSize="sm" color="gray.600">
                Department
              </Text>
              <Text>{request.department || "N/A"}</Text>
            </GridItem>
            <GridItem>
              <Text fontWeight="semibold" fontSize="sm" color="gray.600">
                Category
              </Text>
              <Text>{request.category || "N/A"}</Text>
            </GridItem>
            <GridItem>
              <Text fontWeight="semibold" fontSize="sm" color="gray.600">
                Priority
              </Text>
              {request.priority ? (
                <Badge
                  colorScheme={
                    request.priority === "URGENT"
                      ? "red"
                      : request.priority === "HIGH"
                        ? "orange"
                        : request.priority === "MEDIUM"
                          ? "blue"
                          : "gray"
                  }
                >
                  {request.priority}
                </Badge>
              ) : (
                <Text>N/A</Text>
              )}
            </GridItem>
            <GridItem>
              <Text fontWeight="semibold" fontSize="sm" color="gray.600">
                Estimated Budget
              </Text>
              <Text>₹{request.estimatedBudget || "N/A"}</Text>
            </GridItem>
            <GridItem>
              <Text fontWeight="semibold" fontSize="sm" color="gray.600">
                Expected Delivery
              </Text>
              <Text>
                {request.expectedDeliveryDate
                  ? new Date(request.expectedDeliveryDate).toLocaleDateString()
                  : "N/A"}
              </Text>
            </GridItem>
          </Grid>

          <Divider my={4} />

          <VStack align="stretch" spacing={4}>
            <Box>
              <Text fontWeight="semibold" fontSize="sm" color="gray.600" mb={2}>
                Description
              </Text>
              <Text>{request.description}</Text>
            </Box>

            {request.justification && (
              <Box>
                <Text
                  fontWeight="semibold"
                  fontSize="sm"
                  color="gray.600"
                  mb={2}
                >
                  Justification
                </Text>
                <Text>{request.justification}</Text>
              </Box>
            )}
          </VStack>
        </CardBody>
      </Card>

      {/* Workflow Stage Card - Show for submitted/approved/rejected requests */}
      {request.workflowId && request.status !== "DRAFT" && (
        <Card mb={6} bg={bgColor}>
          <CardHeader>
            <Heading size="md">Approval Workflow</Heading>
          </CardHeader>
          <CardBody>
            {/* Current Stage Info */}
            <VStack align="stretch" spacing={4} mb={6}>
              <Flex justify="space-between" align="center">
                <Box>
                  <Text fontSize="sm" color="gray.600" mb={1}>
                    Current Stage
                  </Text>
                  <Text fontSize="lg" fontWeight="semibold">
                    {request.workflowId?.stages?.[request.workflowStage]
                      ?.stageName || "N/A"}
                  </Text>
                </Box>
                {request.currentApprover && (
                  <Box textAlign="right">
                    <Text fontSize="sm" color="gray.600" mb={1}>
                      Current Approver
                    </Text>
                    <Text fontSize="md" fontWeight="medium">
                      {request.currentApprover?.name || "N/A"}
                    </Text>
                    <Text fontSize="sm" color="gray.500">
                      {request.currentApprover?.email || ""}
                    </Text>
                  </Box>
                )}
              </Flex>

              {/* Stage Progress Bar */}
              {request.workflowId?.stages && (
                <Box>
                  <Flex justify="space-between" mb={2}>
                    {request.workflowId.stages.map((stage, index) => {
                      const isCompleted = index < request.workflowStage;
                      const isCurrent = index === request.workflowStage;
                      const isPending = index > request.workflowStage;

                      // Check if this stage was rejected
                      const stageHistory = request.approvalHistory?.find(
                        (h) => h.stage === index && h.action === "REJECT",
                      );
                      const isRejected = !!stageHistory;

                      return (
                        <VStack
                          key={index}
                          flex={1}
                          spacing={2}
                          position="relative"
                        >
                          {/* Connector Line */}
                          {index > 0 && (
                            <Box
                              position="absolute"
                              left="-50%"
                              right="50%"
                              top="12px"
                              height="2px"
                              bg={
                                isCompleted || isCurrent
                                  ? "green.400"
                                  : "gray.300"
                              }
                              zIndex={0}
                            />
                          )}

                          {/* Stage Icon */}
                          <Icon
                            as={
                              isRejected
                                ? BsXCircleFill
                                : isCompleted
                                  ? BsCheckCircleFill
                                  : isCurrent
                                    ? BsCircleFill
                                    : BsCircleFill
                            }
                            boxSize={6}
                            color={
                              isRejected
                                ? "red.500"
                                : isCompleted
                                  ? "green.500"
                                  : isCurrent
                                    ? "blue.500"
                                    : "gray.300"
                            }
                            zIndex={1}
                            bg={bgColor}
                          />

                          {/* Stage Name */}
                          <Text
                            fontSize="xs"
                            fontWeight={isCurrent ? "bold" : "normal"}
                            color={
                              isRejected
                                ? "red.600"
                                : isCompleted
                                  ? "green.600"
                                  : isCurrent
                                    ? "blue.600"
                                    : "gray.500"
                            }
                            textAlign="center"
                          >
                            {stage.stageName}
                          </Text>
                        </VStack>
                      );
                    })}
                  </Flex>
                </Box>
              )}
            </VStack>

            <Divider my={4} />

            {/* Approval History Timeline */}
            {request.approvalHistory && request.approvalHistory.length > 0 && (
              <Box>
                <Text fontWeight="semibold" fontSize="md" mb={4}>
                  Approval History
                </Text>
                <VStack align="stretch" spacing={4}>
                  {request.approvalHistory.map((history, index) => {
                    const isLast = index === request.approvalHistory.length - 1;

                    return (
                      <Flex key={index} gap={4}>
                        <Box
                          flexShrink={0}
                          position="relative"
                          display="flex"
                          justifyContent="center"
                          minW="20px"
                        >
                          {!isLast && (
                            <Box
                              position="absolute"
                              top="20px"
                              bottom="-20px"
                              left="50%"
                              transform="translateX(-50%)"
                              width="2px"
                              bg="gray.300"
                            />
                          )}
                          <Icon
                            as={
                              history.action === "APPROVE"
                                ? BsCheckCircleFill
                                : history.action === "REJECT"
                                  ? BsXCircleFill
                                  : BsArrowCounterclockwise
                            }
                            boxSize={5}
                            color={
                              history.action === "APPROVE"
                                ? "green.500"
                                : history.action === "REJECT"
                                  ? "red.500"
                                  : "orange.500"
                            }
                            bg={bgColor}
                            zIndex={1}
                          />
                        </Box>
                        <Box flex={1}>
                          <Flex justify="space-between" align="center" mb={1}>
                            <Text fontWeight="semibold" fontSize="sm">
                              {history.stageName ||
                                `Stage ${history.stage + 1}`}
                            </Text>
                            <Badge
                              colorScheme={
                                history.action === "APPROVE"
                                  ? "green"
                                  : history.action === "REJECT"
                                    ? "red"
                                    : "orange"
                              }
                            >
                              {history.action}
                            </Badge>
                          </Flex>
                          <Text fontSize="sm" color="gray.600">
                            {history.approverId?.name || "Unknown"} (
                            {history.approverId?.email})
                          </Text>
                          <Text fontSize="xs" color="gray.500">
                            {new Date(
                              history.actionDate || history.createdAt,
                            ).toLocaleString()}
                          </Text>
                          {history.remarks && (
                            <Text fontSize="sm" mt={2} color="gray.700">
                              <strong>Remarks:</strong> {history.remarks}
                            </Text>
                          )}
                        </Box>
                      </Flex>
                    );
                  })}
                </VStack>
              </Box>
            )}
          </CardBody>
        </Card>
      )}

      {/* Line Items Card */}
      <Card mb={6} bg={bgColor}>
        <CardHeader>
          <Heading size="md">Line Items</Heading>
        </CardHeader>
        <CardBody>
          <LineItemsTable
            lineItems={request.lineItems || []}
            setLineItems={() => {}}
            readOnly={true}
          />
        </CardBody>
      </Card>

      {/* Vendor PO Proposals Section - Show for all statuses */}
      <Card mb={6} bg={bgColor}>
        <CardBody>
          <VendorPOProposalList
            requestId={id}
            requestStatus={request.status}
            onStatusChange={(newStatus) => {
              setRequest({ ...request, status: newStatus });
            }}
          />
        </CardBody>
      </Card>

      {/* Metadata */}
      <Card bg={bgColor}>
        <CardBody>
          <Grid templateColumns="repeat(2, 1fr)" gap={4}>
            <GridItem>
              <Text fontWeight="semibold" fontSize="sm" color="gray.600">
                Created By
              </Text>
              <Text>{request.createdBy?.name || "N/A"}</Text>
            </GridItem>
            <GridItem>
              <Text fontWeight="semibold" fontSize="sm" color="gray.600">
                Created Date
              </Text>
              <Text>{new Date(request.createdAt).toLocaleString()}</Text>
            </GridItem>
            {request.updatedAt && (
              <GridItem>
                <Text fontWeight="semibold" fontSize="sm" color="gray.600">
                  Last Updated
                </Text>
                <Text>{new Date(request.updatedAt).toLocaleString()}</Text>
              </GridItem>
            )}
          </Grid>
        </CardBody>
      </Card>
    </Box>
  );
};

export default RequestDetail;
